import React from 'react';
import { defineMessages } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { CarryOutOutlined,DeleteOutlined, EditOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { AvatarField, BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Button, Flex, Tooltip } from 'antd';

import DriverForm from './DriverForm';

const message = defineMessages({
    objectName: 'Driver',
});

const DriverListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { restaurantId } = useParams();

    const {
        data,
        mixinFuncs,
        queryFilter,
        detail,
        setDetail,
        loading,
        pagination,
        titleModal,
        executeSave,
        loadingSave,
        openModal,
        onShowModal,
        onCancelModal,
        form,
        isEditing,
        onSave,
    } = useListModal({
        apiConfig: apiConfig.driver,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => ({
                delete: (record) => {
                    const hasPerm = funcs.hasPermission([apiConfig.driver.delete.baseURL]);
                    return (
                        <BaseTooltip type="delete" objectName={translate.formatMessage(message.objectName)}>
                            <Button
                                type="link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    mixinFuncs.showDeleteItemConfirm(record.id);
                                }}
                                disabled={!hasPerm}
                                style={{ padding: 0 }}
                            >
                                <DeleteOutlined style={{ color: !hasPerm ? 'gray' : 'red' }} />
                            </Button>
                        </BaseTooltip>
                    );
                },
                edit: (dataRow) => {
                    const hasPerm = funcs.hasPermission([apiConfig.driver.update.baseURL]);
                    return (
                        <BaseTooltip type="edit" objectName={translate.formatMessage(message.objectName)}>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDetail(dataRow);
                                    onShowModal();
                                }}
                                disabled={!hasPerm}
                            >
                                <EditOutlined style={{ color: !hasPerm ? 'gray' : '#428ef6' }} />
                            </Button>
                        </BaseTooltip>
                    );
                },
                deliveryTrip: (dataRow) => {
                    return (
                        <Tooltip title="Delivery Trip">
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/${restaurantId}/driver/${dataRow.id}/delivery-trip?driverName=${encodeURIComponent(dataRow?.fullName || '')}`);
                                }}
                            >
                                <CarryOutOutlined style={{ color: '#52c41a' }} />
                            </Button>
                        </Tooltip>
                    );
                },
            });
        },
    });

    const columns = [
        {
            title: 'Avatar',
            dataIndex: 'avatarPath',
            align: 'center',
            width: 100,
            render: (avatar) => (
                <AvatarField
                    size="large"
                    icon={<UserOutlined />}
                    src={avatar ? `${AppConstants.contentRootUrl}${avatar}` : null}
                />
            ),
        },
        {
            title: translate.formatMessage(commonMessage.fullName),
            dataIndex: ['fullName'],
            render: (username, dataRow) => {
                const hasPerm = mixinFuncs.hasPermission([apiConfig.deliveryTrip.getList.baseURL]);
                return hasPerm ? (
                    <BaseTooltip title={translate.formatMessage(commonMessage.viewDeliveryTrip)}>
                        <Button type="link" onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${restaurantId}/driver/${dataRow.id}/delivery-trip?driverName=${encodeURIComponent(dataRow?.fullName || '')}`);
                        }} style={{ padding: 0 }}>
                            {username}
                        </Button>
                    </BaseTooltip>
                ) : (
                    <span>{username}</span>
                );

            },
        },
        {
            title: translate.formatMessage(commonMessage.username),
            dataIndex: ['username'],
            width: 260,
        },
        {
            title: translate.formatMessage(commonMessage.phone),
            dataIndex: 'phone',
            width: 200,
        },
        mixinFuncs.renderStatusColumn({ 
            width: 120,
        }),
        mixinFuncs.renderActionColumn(
            {
                // deliveryTrip: mixinFuncs.hasPermission([apiConfig.deliveryTrip.getList.baseURL]),
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'fullName',
            placeholder: translate.formatMessage(commonMessage.fullName),
        },
    ];

    const handleOpenCreateModal = () => {
        setDetail(null);
        onShowModal();
    };

    return (
        <PageWrapper routes={[ { breadcrumbName: translate.formatMessage(message.objectName) } ]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={
                    <Flex justify="end">
                        <Button type="primary" onClick={handleOpenCreateModal}>
                            <PlusOutlined />
                        </Button>
                    </Flex>
                    
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading || loadingSave}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    form={form}
                    onDelete={detail ? () => mixinFuncs.handleDelete(detail.id) : null}
                    isEditing={isEditing}
                    width="750px"
                    loading={loadingSave}
                >
                    <DriverForm
                        detail={detail}
                        form={form}
                        onCancel={onCancelModal}
                        isEditing={isEditing}
                        mixinFuncs={mixinFuncs}
                        executeSave={executeSave}
                        onSave={onSave}
                        loadingSave={loadingSave}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default DriverListPage;
